


















































































































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import {
  IExternalAccount,
  IDelegatedAccount,
  LanguageEnum,
  CountryEnum,
  IUserTokenData,
} from '@/api/types';
import {
  listGroups,
  listAccounts,
  putAccount,
  IPutAccountRequest,
  IAddDelegatedAccountsRequest,
  addDelegatedAccounts,
} from '@/api/users';
import { formatApiError } from '@/utils/index';
import { translateRouteTitle } from '@/utils/permission';

type ItemType = {
  text: string;
  value: string;
};

type WithIsActive = {
  isActive: boolean;
};

type EditingDelegatedAccount = IAddDelegatedAccountsRequest.DelegatedAccountInfo & {
  accountId: string;
  showPassword: boolean;
};

@Component({
  name: 'AccountDetail',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  private supplierId = '';
  private loading = true;
  private valid = false;
  private editingAccount = {} as IPutAccountRequest & WithIsActive;
  private editingDelegatedAccoutns: EditingDelegatedAccount[] = [];
  private showEditingPassword = false;
  private languageItems: ItemType[] = Array.from(Object.values(LanguageEnum))
    .filter(l => (l as LanguageEnum) !== LanguageEnum.UNKNOWN_LANGUAGE)
    .map(l => ({
      text: l,
      value: l,
    }));
  private countryItems: ItemType[] = Array.from(Object.values(CountryEnum)).map(
    c => ({
      text: c,
      value: c,
    })
  );
  private groupItems: ItemType[] = [];
  private accounts: Map<string, IExternalAccount> = new Map();
  private accountItems: ItemType[] = [];
  private redirectAfterCreate = true;
  private showRedirectAfterCreate = true;
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';
  private showCopyAccounts = false;
  private canCopyAccounts = false;
  private copyAccountId = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get canCreate() {
    return this.valid;
  }

  created() {
    this.fetchData();
    this.reset();
  }

  private async fetchData(noLoading = false) {
    if (!noLoading) {
      this.loading = true;
      AppModule.StartProgress();
    }

    await Promise.all([
      this.fetchThenLoadSupplierId(),
      this.fetchThenLoadGroups(),
      this.fetchThenLoadAccounts(),
    ]);

    if (!noLoading) {
      this.loading = false;
      AppModule.StopProgress();
    }
  }

  private fetchThenLoadSupplierId() {
    this.supplierId = (JSON.parse(
      UserModule.token
    ) as IUserTokenData).supplierId;
  }

  private async fetchThenLoadGroups() {
    try {
      const res = await listGroups({});
      this.groupItems = res.groups.map(g => ({
        text: g.name,
        value: g.id!,
      }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async fetchThenLoadAccounts() {
    try {
      const res = await listAccounts({});
      this.accounts = new Map(res.accounts.map(a => [a.id, a]));
      this.accountItems = res.accounts.map(a => ({
        text: a.username,
        value: a.id,
      }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.showRedirectAfterCreate = true;
    this.showCopyAccounts = false;
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForCreate() {
    this.dialogTextTitle = `${this.translateRouteTitle(
      this.$route.meta.title
    )}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = true;
    this.showCopyAccounts = false;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.createAccount();
    };
    this.dialog = true;
  }

  private showDialogForReset() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = false;
    this.showCopyAccounts = false;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.reset();
    };
    this.dialog = true;
  }

  private showDialogForCopy() {
    this.dialogTextTitle = `${this.$t('Copy from other {msg}', {
      msg: this.$t('Account'),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = false;
    this.showCopyAccounts = true;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.copyAccount();
    };
    this.dialog = true;
  }

  private copyAccount() {
    const copyAccount = this.accounts.get(this.copyAccountId)!;
    this.editingAccount = {
      username: '',
      password: '',
      groupId: copyAccount.groupId,
      locale: copyAccount.locale,
      fullName: '',
      isInactive: copyAccount.isInactive,
      isActive: !copyAccount.isInactive,
    };
    this.editingDelegatedAccoutns = copyAccount.delegatedAccounts.map(a => ({
      accountId: a.accountId,
      showPassword: false,
      needPassword: false,
      supplierId: a.supplierId,
      username: this.accounts.get(a.accountId)!.username,
      password: '',
    }));
  }

  private reset() {
    this.editingAccount = {
      username: '',
      password: '',
      groupId: '',
      locale: {
        language: LanguageEnum.JAPANESE,
        country: CountryEnum.UNIVERSAL,
      },
      fullName: '',
      isInactive: false,
      isActive: true,
    };
    this.editingDelegatedAccoutns = [];
  }

  private async createAccount() {
    this.loading = true;
    AppModule.StartProgress();

    try {
      const newAccount = JSON.parse(
        JSON.stringify(this.editingAccount)
      ) as IPutAccountRequest;

      const req: IPutAccountRequest = {
        username: newAccount.username,
        password: newAccount.password,
        groupId: newAccount.groupId,
        locale: newAccount.locale,
        fullName: newAccount.fullName,
        isInactive: false,
      };

      const res = await putAccount(req);
      await this.updateDelegatedAccounts(res.id);
      await this.postCreate(res.id);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.translateRouteTitle(
            this.$route.meta.title
          ) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
      await this.postCreate();
    }

    AppModule.StopProgress();
    this.loading = false;
  }

  private async updateDelegatedAccounts(accountId: string) {
    const addDelegatedAccountsMap: Map<
      string,
      IAddDelegatedAccountsRequest.DelegatedAccountInfo
    > = new Map();
    this.editingDelegatedAccoutns.forEach(a => {
      addDelegatedAccountsMap.set(a.accountId, {
        supplierId: a.supplierId,
        username: a.username,
        password: a.password,
      });
    });

    try {
      await Promise.all([
        this.addDelegatedAccounts(
          Array.from(addDelegatedAccountsMap.values()),
          accountId
        ),
      ]);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Update {msg}', {
            msg: this.$t('Delegated account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async addDelegatedAccounts(
    delegatedAccounts: IAddDelegatedAccountsRequest.DelegatedAccountInfo[],
    accountId: string
  ) {
    if (delegatedAccounts.length > 0) {
      await addDelegatedAccounts({
        accountId: accountId,
        delegatedAccounts: delegatedAccounts,
      });
    }
  }

  private async postCreate(accountId?: string) {
    if (this.redirectAfterCreate && accountId) {
      this.$nextTick(() => {
        this.$router.push(`/account/${accountId}`);
      });
    } else {
      this.reset();
      await this.fetchData(true);

      if (accountId) {
        this.$toast.success(
          this.$t('Succeeded {do}', {
            do: (this.translateRouteTitle(
              this.$route.meta.title
            ) as string).toLowerCase(),
          }) as string,
          {
            icon: this.$route.meta.icon,
          }
        );
      }
    }
  }

  private addEditingDelegatedAccount() {
    this.editingDelegatedAccoutns.push({
      accountId: '',
      showPassword: false,
      supplierId: this.supplierId,
      username: '',
      password: '',
    });
  }

  private removeEditingDelegatedAccount(delegatedAccountIndex: number) {
    this.editingDelegatedAccoutns.splice(delegatedAccountIndex, 1);
  }

  private handleEditingDelegatedAccountIdInput(
    accountId: string,
    delegatedAccountIndex: number
  ) {
    this.editingDelegatedAccoutns[
      delegatedAccountIndex
    ].username = this.accounts.get(accountId)!.username;
  }

  private getRequiredRules(
    msg: any,
    exception: (v: any) => boolean = () => false
  ) {
    return [
      (v: string) =>
        exception(v) ||
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$te(msg) ? this.$t(msg) : msg,
        }) as string),
    ];
  }

  private translateRouteTitle(title: string) {
    return translateRouteTitle(this.$i18n, title);
  }
}
